import type { MenuSchemaEntry } from '@bucketplace/design-system/bds';
import { IconHouse } from '@bucketplace/icons';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { CONTENT_HOST, OHS_HOST } from '@/common';
import { useTRPC } from '@/trpc/utils';
import type { Topic } from '@/types';

const formatTopics = (topics: Topic[]): Topics => {
  /** recommendTopic 찾는 순서
   * 1. defaultTopic = true
   * 2. key = 'recommend'(DEFAULT_TOPIC_KEY)
   * 3. topics 중 첫번째 item
   */
  const recommendTopic =
    topics.find(
      ({ defaultTopic, key }) => defaultTopic || key === 'recommend',
    ) ?? topics[0];
  const lifeTopics = topics.filter((topic) => topic !== recommendTopic);

  return {
    default: recommendTopic,
    fixed: lifeTopics,
  };
};

const createTopicMenu = (topics: Topic[]): MenuSchemaEntry[] => {
  return topics.map(({ name, key }) => ({
    title: `${name}`,
    href: `${CONTENT_HOST}/topics/${key}`,
    logId: key,
  }));
};

const initialTopics: Topics = {
  default: { key: '', name: '' },
  fixed: [],
};

interface Topics {
  default: Topic;
  fixed: Topic[];
}

export const useCreateHnCMenu = (): MenuSchemaEntry[] => {
  const trpc = useTRPC();
  const { data } = useQuery(trpc.navigationHnc.queryOptions());
  const topics = useMemo(
    () => (data?.topics ? formatTopics(data.topics) : initialTopics),
    [data?.topics],
  );

  const newMenu = useMemo(
    () => [
      {
        title: '커뮤니티',
        icon: <IconHouse size={24} weight="regular" />,
        href: `${OHS_HOST}`,
        entries: [
          {
            title: '홈',
            href: `${OHS_HOST}`,
            logId: 'home',
          },
          ...createTopicMenu([topics.default]),
          {
            title: '집들이',
            href: `${OHS_HOST}/projects?writer=self`,
            logId: 'project',
          },
          {
            title: '집사진',
            href: `${OHS_HOST}/contents/card_collections`,
            logId: 'card',
          },
          ...createTopicMenu(topics.fixed),
          {
            title: '3D인테리어',
            href: `${OHS_HOST}/interior3ds`,
            logId: 'interior3d',
          },
          {
            title: '이벤트',
            href: `${OHS_HOST}/competitions/feed`,
            logId: 'event',
          },
        ].filter(Boolean) as MenuSchemaEntry[],
      },
    ],
    [topics.default, topics.fixed],
  );

  return newMenu;
};
